import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { FinalCTA } from "@/components/home/FinalCTA";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  payrollServiceSchema,
  payrollFaqSchema,
  generateBreadcrumbSchema,
} from "@/lib/structuredData";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Users,
  FileText,
  Shield,
  Clock,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

const payrollFeatures = [
  {
    icon: Users,
    title: "Monthly Payroll Processing",
    description:
      "We calculate salaries, deductions, and net pay for all employees. Payslips are generated and distributed each month.",
  },
  {
    icon: FileText,
    title: "EMP201 Monthly Submissions",
    description:
      "EMP201 is a monthly employer declaration to SARS reporting PAYE, UIF, and SDL. We submit by the 7th of every month to keep you penalty-free.",
  },
  {
    icon: Shield,
    title: "EMP501 Bi-Annual Reconciliation",
    description:
      "EMP501 reconciles all monthly EMP201 submissions for the tax period. We submit during both the interim (Sep–Oct) and annual (Apr–May) windows.",
  },
  {
    icon: Clock,
    title: "UIF Monthly Declarations",
    description:
      "Unemployment Insurance Fund contributions (1% employer + 1% employee) are declared monthly to the Department of Labour via the EMP201.",
  },
];

const deadlines = [
  { submission: "EMP201", frequency: "Monthly", deadline: "7th of each month", penalty: "Up to 10% + interest" },
  { submission: "EMP501 (Interim)", frequency: "Bi-annual", deadline: "September – October", penalty: "Penalties per month outstanding" },
  { submission: "EMP501 (Annual)", frequency: "Bi-annual", deadline: "April – May", penalty: "Penalties per month outstanding" },
  { submission: "UIF Declaration", frequency: "Monthly", deadline: "Via EMP201 (7th monthly)", penalty: "Compliance action by DoL" },
];

const PayrollServices = () => {
  return (
    <Layout>
      <SEO
        title="Payroll Services SA | EMP201, EMP501, UIF | From R2,500/month"
        description="SARS-compliant payroll processing for South African businesses. EMP201 monthly submissions, EMP501 reconciliations, UIF declarations — all included from R2,500/month. SAICA registered."
        canonical="/payroll-services"
        structuredData={[
          payrollServiceSchema,
          payrollFaqSchema,
          generateBreadcrumbSchema([
            { name: "Home", url: "https://bookkeepingservices.co.za/" },
            { name: "Services", url: "https://bookkeepingservices.co.za/services" },
            { name: "Payroll Services", url: "https://bookkeepingservices.co.za/payroll-services" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/30" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-sm font-medium text-primary mb-4 tracking-wide uppercase animate-fade-up">
              Payroll Services
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 animate-fade-up">
              Payroll, EMP201, EMP501 & UIF
              <span className="block text-primary">Made Simple</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed animate-fade-up max-w-2xl mx-auto mb-8">
              Payroll processing is the calculation and distribution of employee salaries, 
              including all statutory deductions and submissions to SARS. We handle every 
              aspect — so you never miss a deadline.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up">
              <Link to="/book-a-call">
                <Button size="lg" className="btn-primary gap-2 text-base px-8 h-14">
                  Get a Free Quote
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/packages">
                <Button size="lg" variant="outline" className="btn-outline-primary gap-2 text-base px-8 h-14">
                  View Packages
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included — answer blocks for AI */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="What's Included"
            title="Complete Payroll Management"
            description="Every payroll submission handled by a SAICA registered Chartered Accountant."
          />
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {payrollFeatures.map((feature) => (
              <div key={feature.title} className="card-elevated p-8">
                <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center text-primary mb-5">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Deadlines Table — AI-optimised structured content */}
      <section className="py-20 lg:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="Deadlines"
            title="SARS Payroll Submission Deadlines"
            description="Missing these deadlines costs money. We ensure every submission is on time, every month."
          />
          <div className="max-w-4xl mx-auto">
            <div className="bg-card rounded-xl border border-border overflow-hidden shadow-soft">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-accent">
                      <th className="text-left p-4 font-heading font-semibold text-foreground">Submission</th>
                      <th className="text-left p-4 font-heading font-semibold text-foreground">Frequency</th>
                      <th className="text-left p-4 font-heading font-semibold text-foreground">Deadline</th>
                      <th className="text-left p-4 font-heading font-semibold text-foreground">Penalty for Late</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deadlines.map((row, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="p-4 font-medium text-foreground">{row.submission}</td>
                        <td className="p-4 text-muted-foreground">{row.frequency}</td>
                        <td className="p-4 text-muted-foreground">{row.deadline}</td>
                        <td className="p-4">
                          <span className="inline-flex items-center gap-1 text-destructive text-xs font-medium">
                            <AlertTriangle className="w-3 h-3" />
                            {row.penalty}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us for Payroll */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="Why Us"
            title="Why Choose Us for Payroll?"
            description="E-E-A-T: Experience, Expertise, Authoritativeness, and Trust."
          />
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              "SAICA registered Chartered Accountant — professional oversight on every submission",
              "Zero missed deadlines — automated reminders and proactive submission workflow",
              "Packages from R2,500/month including payroll for up to 5 employees",
              "IRP5 and IT3(a) certificates generated for employees at year-end",
              "Confidential processing compliant with POPIA data protection",
              "48-hour onboarding — we can start processing your payroll this week",
            ].map((point, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-muted/50">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm text-foreground">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="FAQ"
            title="Payroll Questions Answered"
            description="Common questions about payroll, EMP201, EMP501, and UIF in South Africa."
          />
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-3">
              {payrollFaqSchema.mainEntity.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-card border border-border rounded-xl px-6 hover:border-primary/20 transition-colors duration-300 data-[state=open]:border-primary/30 data-[state=open]:shadow-md"
                >
                  <AccordionTrigger className="text-left font-heading text-base font-semibold hover:no-underline hover:text-primary transition-colors py-5">
                    {faq.name}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                    {faq.acceptedAnswer.text}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <FinalCTA />
    </Layout>
  );
};

export default PayrollServices;
